import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", start: "", end: "", description: "" };

export default function CalendarPage() {
  const { t } = useLang();
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/calendar/events");
      setEvents(data);
    } catch (error) {
      console.error("Failed to load calendar events:", error);
      toast.error("Failed to load calendar events");
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try { await api.post("/calendar/events", form); toast.success(t("common.create")); setOpen(false); setForm(empty); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/calendar/events/${id}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // group by day
  const groups = events.reduce((acc, e) => {
    const d = (e.start || "").slice(0, 10);
    (acc[d] = acc[d] || []).push(e);
    return acc;
  }, {});
  const days = Object.keys(groups).sort();

  return (
    <DashboardLayout title={t("calendar.title")} subtitle={t("calendar.subtitle")}
      right={<button data-testid="event-new" onClick={() => setOpen(true)} className="btn-scale h-9 px-3 rounded-md bg-blue-500 text-white font-medium text-sm flex items-center gap-2 hover:bg-blue-400"><Plus className="w-4 h-4" /> {t("calendar.new")}</button>}>
      <div className="space-y-4" data-testid="calendar-list">
        {days.map((d) => (
          <div key={d} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
            <div className="overline mb-3 text-blue-400">{d}</div>
            <div className="space-y-2">
              {groups[d].map((e) => (
                <div key={e.event_id} className="flex items-center justify-between p-2 rounded-md hover:bg-zinc-900" data-testid={`event-${e.event_id}`}>
                  <div>
                    <div className="text-sm text-zinc-100">{e.title}</div>
                    <div className="text-xs text-zinc-500">{e.start?.slice(11,16)}{e.end ? ` – ${e.end.slice(11,16)}` : ""} · {e.description || ""}</div>
                  </div>
                  <button onClick={() => remove(e.event_id)} className="h-7 w-7 grid place-items-center rounded text-zinc-400 hover:text-rose-400 hover:bg-zinc-800"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          </div>
        ))}
        {days.length === 0 && <div className="p-8 text-center text-sm text-zinc-500 border border-dashed border-zinc-800 rounded-xl">{t("common.no_data")}</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="event-dialog">
          <DialogHeader><DialogTitle>{t("calendar.new")}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <input data-testid="event-field-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
            <input data-testid="event-field-start" type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
            <input data-testid="event-field-end" type="datetime-local" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
            <textarea data-testid="event-field-desc" rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm" />
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-scale h-9 px-3 rounded-md border border-zinc-700 text-zinc-300 text-sm">{t("common.cancel")}</button>
            <button data-testid="event-save" onClick={create} className="btn-scale h-9 px-4 rounded-md bg-blue-500 text-white text-sm font-medium hover:bg-blue-400">{t("common.save")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
